import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Badge,
  Box,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import SendIcon from '@material-ui/icons/Send';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import SearchIcon from '@material-ui/icons/Search';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import DoneIcon from '@material-ui/icons/Done';
import ForumOutlinedIcon from '@material-ui/icons/ForumOutlined';

import CallIcon from '@material-ui/icons/Call';
import VideocamOutlinedIcon from '@material-ui/icons/VideocamOutlined';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { io, Socket } from 'socket.io-client';

import api from '../../../../services/api';
import { Main } from '../../../../components/Main';
import { CallManager } from '../../components/CallManager';

// Central de Mensagens — Fase 1 (texto).
// Realtime por polling curto: conversas a cada 10s, thread aberto a cada 5s.

interface IConversation {
  driverDocument: string;
  driverName: string | null;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  unread: number;
}

interface IMessage {
  id: number;
  direction: 'portal' | 'driver';
  senderName: string | null;
  type: string;
  text: string | null;
  mediaUrl: string | null;
  deletedAt: string | null;
  deliveredAt: string | null;
  readAt: string | null;
  createdAt: string;
}

// Fase 2 — mídia dentro do balão
const MessageMedia = ({ message }: { message: IMessage }) => {
  const [expanded, setExpanded] = useState(false);
  if (!message.mediaUrl) return null;

  if (message.type === 'image') {
    return (
      <>
        <img
          src={message.mediaUrl}
          alt="Imagem enviada"
          style={{
            maxWidth: '100%',
            maxHeight: 220,
            borderRadius: 8,
            cursor: 'zoom-in',
            display: 'block',
          }}
          onClick={() => setExpanded(true)}
        />
        {expanded && (
          <div
            onClick={() => setExpanded(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.85)',
              zIndex: 2000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'zoom-out',
            }}
          >
            <img
              src={message.mediaUrl}
              alt="Imagem ampliada"
              style={{ maxWidth: '92vw', maxHeight: '92vh' }}
            />
          </div>
        )}
      </>
    );
  }

  if (message.type === 'video') {
    return (
      <video
        src={message.mediaUrl}
        controls
        preload="metadata"
        style={{ maxWidth: '100%', maxHeight: 260, borderRadius: 8 }}
      />
    );
  }

  if (message.type === 'audio') {
    return (
      <audio
        src={message.mediaUrl}
        controls
        preload="metadata"
        style={{ maxWidth: 260 }}
      />
    );
  }

  return (
    <a href={message.mediaUrl} target="_blank" rel="noreferrer">
      Abrir anexo
    </a>
  );
};

const formatTime = (value: string) => {
  const date = new Date(value);
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  const time = date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return sameDay ? time : `${date.toLocaleDateString('pt-BR')} ${time}`;
};

export const MessagesCenter = () => {
  const [conversations, setConversations] = useState<IConversation[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');
  const [sending, setSending] = useState(false);
  const [online, setOnline] = useState<string[]>([]);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [callTo, setCallTo] = useState<{
    document: string;
    name: string;
    kind: 'audio' | 'video';
  } | null>(null);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const selectedRef = useRef<string | null>(null);
  selectedRef.current = selected;

  const loadConversations = useCallback(async () => {
    try {
      const response = await api.get('/chat/conversations');
      if (response.data?.conversations)
        setConversations(response.data.conversations);
    } catch {
      // API sem o módulo chat ainda: tela fica vazia, sem quebrar
    }
  }, []);

  const loadMessages = useCallback(
    async (driverDocument: string, reset: boolean) => {
      try {
        const after = reset
          ? undefined
          : (window as any).__chatLastId?.[driverDocument];
        const response = await api.get(
          `/chat/conversations/${driverDocument}/messages${
            after ? `?after=${after}` : ''
          }`,
        );
        const incoming: IMessage[] = response.data?.messages || [];
        const deletedIds: number[] = response.data?.deletedIds || [];
        if (selectedRef.current !== driverDocument) return;

        setMessages((current) => {
          const base = (reset ? [] : current).map((m) =>
            deletedIds.includes(m.id) && !m.deletedAt
              ? {
                  ...m,
                  deletedAt: new Date().toISOString(),
                  text: null,
                  mediaUrl: null,
                }
              : m,
          );
          const known = new Set(base.map((m) => m.id));
          const merged = [
            ...base,
            ...incoming.filter((m) => !known.has(m.id)),
          ];
          if (merged.length > 0) {
            (window as any).__chatLastId = {
              ...((window as any).__chatLastId || {}),
              [driverDocument]: merged[merged.length - 1].id,
            };
          }
          return merged;
        });

        if (incoming.some((m) => m.direction === 'driver')) {
          await api.put(`/chat/conversations/${driverDocument}/read`);
          loadConversations();
        }
      } catch {
        // silencioso: próximo tick tenta de novo
      }
    },
    [loadConversations],
  );

  // vindo do Scorecard de Motoristas: abre direto a conversa
  useEffect(() => {
    const target = localStorage.getItem('chatOpenDriver');
    if (target) {
      localStorage.removeItem('chatOpenDriver');
      setSelected(target);
    }
  }, []);

  // polling (fallback do socket)
  useEffect(() => {
    loadConversations();
    const timer = setInterval(loadConversations, 10000);
    return () => clearInterval(timer);
  }, [loadConversations]);

  // Fase 3: socket — chat instantâneo, presença e sinalização de chamadas
  useEffect(() => {
    const token = localStorage.getItem('@4Log:access_token');
    if (!token) return;
    const instance = io(
      `${process.env.REACT_APP_4LOG_API_URL}/chat-ws`,
      { auth: { token }, transports: ['websocket', 'polling'] },
    );

    instance.on('presence', (data: { online: string[] }) =>
      setOnline(data.online || []),
    );
    instance.on(
      'chat:new',
      (data: { driverDocument: string; message: IMessage }) => {
        loadConversations();
        if (data.driverDocument !== selectedRef.current) return;
        setMessages((current) => {
          if (current.some((m) => m.id === data.message.id)) return current;
          (window as any).__chatLastId = {
            ...((window as any).__chatLastId || {}),
            [data.driverDocument]: data.message.id,
          };
          return [...current, data.message];
        });
        if (data.message.direction === 'driver') {
          api
            .put(`/chat/conversations/${data.driverDocument}/read`)
            .catch(() => undefined);
        }
      },
    );
    instance.on(
      'chat:deleted',
      (data: { driverDocument: string; messageId: number }) => {
        if (data.driverDocument !== selectedRef.current) return;
        setMessages((current) =>
          current.map((m) =>
            m.id === data.messageId
              ? {
                  ...m,
                  deletedAt: new Date().toISOString(),
                  text: null,
                  mediaUrl: null,
                }
              : m,
          ),
        );
      },
    );
    instance.emit('presence:list', null, (data: { online: string[] }) =>
      setOnline(data?.online || []),
    );

    setSocket(instance);
    return () => {
      instance.disconnect();
      setSocket(null);
    };
  }, [loadConversations]);

  // "Apagar para todos" — só nas mensagens do próprio portal
  const deleteForEveryone = useCallback(
    async (messageId: number) => {
      if (!selected) return;
      if (!window.confirm('Apagar esta mensagem para todos?')) return;
      await api.put(
        `/chat/conversations/${selected}/messages/${messageId}/delete`,
      );
      setMessages((current) =>
        current.map((m) =>
          m.id === messageId
            ? {
                ...m,
                deletedAt: new Date().toISOString(),
                text: null,
                mediaUrl: null,
              }
            : m,
        ),
      );
    },
    [selected],
  );

  useEffect(() => {
    if (!selected) return;
    loadMessages(selected, true);
    api.put(`/chat/conversations/${selected}/read`).then(loadConversations);
    const timer = setInterval(() => loadMessages(selected, false), 5000);
    return () => clearInterval(timer);
  }, [selected, loadMessages, loadConversations]);

  // autoscroll para a última mensagem
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Fase 2: envia imagem (botão de anexo ou colar print no campo)
  const sendImage = useCallback(
    async (file: File) => {
      if (!selected || sending) return;
      if (file.size > 10 * 1024 * 1024) {
        window.alert('Imagem acima de 10 MB — reduza e tente de novo.');
        return;
      }
      setSending(true);
      try {
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result));
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        const conversation = conversations.find(
          (c) => c.driverDocument === selected,
        );
        const response = await api.post(
          `/chat/conversations/${selected}/messages`,
          {
            type: 'image',
            mediaBase64: base64,
            mediaExtension:
              file.name.split('.').pop()?.toLowerCase() || 'jpg',
            text: draft.trim() || undefined,
            driverName: conversation?.driverName,
          },
        );
        if (response.data?.message) {
          const message: IMessage = response.data.message;
          setMessages((current) => [...current, message]);
          (window as any).__chatLastId = {
            ...((window as any).__chatLastId || {}),
            [selected]: message.id,
          };
        }
        setDraft('');
        loadConversations();
      } finally {
        setSending(false);
      }
    },
    [selected, sending, conversations, draft, loadConversations],
  );

  const send = useCallback(async () => {
    const text = draft.trim();
    if (!text || !selected || sending) return;
    setSending(true);
    try {
      const conversation = conversations.find(
        (c) => c.driverDocument === selected,
      );
      const response = await api.post(
        `/chat/conversations/${selected}/messages`,
        { text, driverName: conversation?.driverName },
      );
      if (response.data?.message) {
        const message: IMessage = response.data.message;
        setMessages((current) => [...current, message]);
        (window as any).__chatLastId = {
          ...((window as any).__chatLastId || {}),
          [selected]: message.id,
        };
      }
      setDraft('');
      loadConversations();
    } finally {
      setSending(false);
    }
  }, [draft, selected, sending, conversations, loadConversations]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return conversations;
    return conversations.filter(
      (c) =>
        (c.driverName || '').toLowerCase().includes(term) ||
        c.driverDocument.includes(term),
    );
  }, [conversations, search]);

  const current = conversations.find((c) => c.driverDocument === selected);

  return (
    <Main title="Central de Mensagens">
      <CallManager
        socket={socket}
        outgoingTo={callTo}
        onOutgoingHandled={() => setCallTo(null)}
      />
      <Box
        display="flex"
        style={{ gap: 16, height: 'calc(100vh - 180px)', minHeight: 480 }}
      >
        {/* lista de motoristas */}
        <Paper
          style={{
            width: 320,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box p={1.5}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Buscar motorista..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Divider />
          <List style={{ overflowY: 'auto', flex: 1, padding: 0 }}>
            {filtered.map((c) => (
              <ListItem
                key={c.driverDocument}
                button
                selected={c.driverDocument === selected}
                onClick={() => {
                  setMessages([]);
                  setSelected(c.driverDocument);
                }}
                divider
              >
                <FiberManualRecordIcon
                  style={{
                    fontSize: 10,
                    marginRight: 8,
                    color: online.includes(c.driverDocument)
                      ? '#48AE3D'
                      : '#ccc',
                  }}
                />
                <ListItemText
                  primary={c.driverName || c.driverDocument}
                  secondary={c.lastMessagePreview || 'Sem mensagens ainda'}
                  primaryTypographyProps={{
                    style: { fontWeight: c.unread > 0 ? 700 : 500 },
                  }}
                  secondaryTypographyProps={{
                    noWrap: true,
                    style: { fontSize: 12 },
                  }}
                />
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-end"
                  style={{ gap: 4, marginLeft: 8 }}
                >
                  {c.lastMessageAt && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {formatTime(c.lastMessageAt)}
                    </Typography>
                  )}
                  {c.unread > 0 && (
                    <Badge
                      badgeContent={c.unread}
                      color="error"
                      style={{ marginRight: 10 }}
                    />
                  )}
                </Box>
              </ListItem>
            ))}
            {filtered.length === 0 && (
              <Box p={2}>
                <Typography variant="body2" color="textSecondary">
                  Nenhum motorista encontrado.
                </Typography>
              </Box>
            )}
          </List>
        </Paper>

        {/* thread */}
        <Paper
          style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
        >
          {!selected ? (
            <Box
              flex={1}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              style={{ gap: 8, color: '#888' }}
            >
              <ForumOutlinedIcon style={{ fontSize: 48 }} />
              <Typography variant="body1">
                Selecione um motorista para conversar
              </Typography>
              <Typography variant="caption">
                Ele recebe pelo app 4LogX Driver e responde por lá
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                px={2}
                py={1}
                display="flex"
                alignItems="center"
                style={{ gap: 8 }}
              >
                <FiberManualRecordIcon
                  style={{
                    fontSize: 12,
                    color: online.includes(selected) ? '#48AE3D' : '#bbb',
                  }}
                  titleAccess={
                    online.includes(selected)
                      ? 'Online agora'
                      : 'Offline (recebe pelo push/sync)'
                  }
                />
                <Typography
                  variant="subtitle1"
                  style={{ fontWeight: 700, flex: 1 }}
                >
                  {current?.driverName || selected}
                </Typography>
                <IconButton
                  color="primary"
                  onClick={() =>
                    setCallTo({
                      document: selected,
                      name: current?.driverName || selected,
                      kind: 'audio',
                    })
                  }
                  disabled={!socket || !online.includes(selected)}
                  aria-label="Ligar para o motorista"
                  title={
                    online.includes(selected)
                      ? 'Ligar (voz)'
                      : 'Motorista offline — ligação indisponível'
                  }
                >
                  <CallIcon />
                </IconButton>
                <IconButton
                  color="primary"
                  onClick={() =>
                    setCallTo({
                      document: selected,
                      name: current?.driverName || selected,
                      kind: 'video',
                    })
                  }
                  disabled={!socket || !online.includes(selected)}
                  aria-label="Vídeo chamada com o motorista"
                  title={
                    online.includes(selected)
                      ? 'Vídeo chamada'
                      : 'Motorista offline — chamada indisponível'
                  }
                >
                  <VideocamOutlinedIcon />
                </IconButton>
              </Box>
              <Divider />
              <div
                ref={threadRef}
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                {messages.map((m) => {
                  const mine = m.direction === 'portal';
                  return (
                    <div
                      key={m.id}
                      style={{
                        alignSelf: mine ? 'flex-end' : 'flex-start',
                        maxWidth: '70%',
                        background: mine ? '#0E52A0' : '#F0F2F5',
                        color: mine ? '#fff' : '#1c2733',
                        borderRadius: 12,
                        borderBottomRightRadius: mine ? 4 : 12,
                        borderBottomLeftRadius: mine ? 12 : 4,
                        padding: '8px 12px',
                        fontSize: 14,
                        lineHeight: 1.45,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {m.deletedAt ? (
                        <span style={{ fontStyle: 'italic', opacity: 0.7 }}>
                          🚫 Mensagem apagada
                        </span>
                      ) : (
                        <>
                          <MessageMedia message={m} />
                          {m.text}
                        </>
                      )}
                      <div
                        style={{
                          fontSize: 10,
                          opacity: 0.75,
                          marginTop: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          justifyContent: 'flex-end',
                        }}
                      >
                        {mine && !m.deletedAt && m.type !== 'call' && (
                          <DeleteOutlineIcon
                            style={{
                              fontSize: 13,
                              cursor: 'pointer',
                              opacity: 0.6,
                            }}
                            titleAccess="Apagar para todos"
                            onClick={() => deleteForEveryone(m.id)}
                          />
                        )}
                        {formatTime(m.createdAt)}
                        {mine &&
                          (m.readAt ? (
                            <DoneAllIcon style={{ fontSize: 13 }} />
                          ) : m.deliveredAt ? (
                            <DoneAllIcon
                              style={{ fontSize: 13, opacity: 0.5 }}
                            />
                          ) : (
                            <DoneIcon style={{ fontSize: 13 }} />
                          ))}
                      </div>
                    </div>
                  );
                })}
                {messages.length === 0 && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ margin: 'auto' }}
                  >
                    Comece a conversa — o motorista recebe no app.
                  </Typography>
                )}
              </div>
              <Divider />
              <Box display="flex" alignItems="center" p={1.5} style={{ gap: 8 }}>
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  id="chat-attach-input"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) sendImage(file);
                    e.target.value = '';
                  }}
                />
                <label htmlFor="chat-attach-input">
                  <IconButton
                    component="span"
                    disabled={sending}
                    aria-label="Anexar imagem"
                  >
                    <AttachFileIcon />
                  </IconButton>
                </label>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder="Escreva uma mensagem ou cole um print..."
                  value={draft}
                  multiline
                  maxRows={4}
                  onChange={(e) => setDraft(e.target.value)}
                  onPaste={(e) => {
                    const item = Array.from(e.clipboardData.items).find(
                      (i) => i.type.startsWith('image/'),
                    );
                    const file = item?.getAsFile();
                    if (file) {
                      e.preventDefault();
                      sendImage(file);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                />
                <IconButton
                  color="primary"
                  onClick={send}
                  disabled={!draft.trim() || sending}
                  aria-label="Enviar"
                >
                  <SendIcon />
                </IconButton>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Main>
  );
};
